import _ from 'lodash'
import React, { lazy, useEffect, useMemo, useRef } from 'react'
import { CssBaseline, StylesProvider, ThemeProvider } from '@material-ui/core'
import { theme as mainTheme } from '../lib/getProjectPageContext'
import { ftProjectUpdateParams } from '../store/project'
import { IS_SERVER } from '../constants/ssr'
import { IS_INTERNAL_PREVIEW } from '../constants/internalPreview'
import rtl from 'jss-rtl'
import { create } from 'jss'
import { jssPreset } from '@material-ui/core/styles'
import { v4 as uuid } from 'uuid'
import {
  projectPreviewSetOnLoadData,
  projectPreviewDetectLocationAPI,
} from '../store/project/projectPreviewSetOnLoadData'
import { processWidgetSFMapping } from '../store/project/processWidgetSFMapping'
import { processSFAutoTrigger } from '../store/project/processSFAutoTrigger'
import { processRules } from '../store/project/processRules'
import {
  processPageOnLoad,
  processSetSessLeftTime,
  processSetOtpSessionLeftTime,
} from '../store/project/processPageOnLoad'
import { initSFAutoTrigger } from '../store/project/initSFAutoTrigger'
import { useDispatch, useSelector } from 'react-redux'
import { SFActionProcessor } from '../components/WidgetPreview/SFActionProcessor'
import { ProjectStyles } from '../components/WidgetBuilder/UI/ProjectStyles'
import { LayoutProvider } from '../hooks/useLayoutMode'
import { DEVICE_DEFAULT_MAX_SIZE } from '../lib/DEVICE_DEFAULT_MAX_SIZE'
import { ProjectParameterSync } from '../components/WidgetPreview/ProjectParameterSync'
import {
  getProjectLocalKey,
  getSessionProjectLocaKey,
  ProjectPersistSessionParameter,
  VideoEventsAPI,
  SESS_COOKIE_KEY,
} from '../components/WidgetPreview/ProjectPersistSessionParameter'
import { getQueryVariable } from '../lib/processPortalRules'
import { setProjectPreviewStore } from '../store/project/actions'
import { StyleElement } from '../components/Main/StyleElement'
import { Map, List, isImmutable } from 'immutable'
import { CHAT_BOT_TYPE, SLACK_BOT_TYPE, SURVEY_EMAIL_TYPE, WEB_FORM_TYPE } from '../constants/features'
import { getCdnHost, CloudResourceType } from '../lib/env-variables'
import { COOKIE_ERROR, ftGetLocalStorage, ftRemoveLocalStorage, ftSetLocalStorage } from '../lib/common'
import { DEFAULT_LOADERS } from '../constants/DEFAULT_LOADERS'
import { decompressFromUTF16 } from 'lz-string'
import { ToastProvider, DefaultToast } from 'react-toast-notifications'
import { useGlobalCssVariables } from '../hooks/useGlobalCssVariables'
import { useLangDirection } from '../hooks/useLangDirection'
import clsx from 'clsx'
import { isSame } from '../components/WidgetBuilder/UI/isSame'
import { processConditionQueCache } from '../store/project/processCondCache'
import { loadAppPrevStateIfExist } from '../store/project/pwa-tools'
import { processButtonAction } from '../store/project/processButtonAction'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { STATIC_SF_FONT } from '../constants/FontFamily'
import { SuspenseLoader } from './WebLoader'
import { ProfileProvider } from '../components/Project/Context/ProfileContext'
import { JSScript } from '../components/Project/ProjectUI/AsssetsManager'
import { SESSION_SALT_NAME, SESSION_TOKEN_NAME, OTP_SESSION_IS_ACTIVE } from '../constants/session'
import { getBuilderTypePage } from '../store/project/builderTypePage'
import { getAxios, isFirstTimeLogin } from '../lib/projectAxios'
import { setClearSmartVParams, setTargetParamsValue } from '../store/project/setTargetParamValue'
import WebStatsigProvider from '../lib/statsig/provider'
import { FeatureStoreDataProvider } from '../lib/statsig/FeatureStoreDataProvider'

const BuilderAPI = lazy(() =>
  import('../components/Project/UI/BuilderAPI').then((val) => ({ default: val.BuilderAPI }))
)
const ChatBotApi = lazy(() =>
  import('../components/Project/UI/ChatBotApi').then((val) => ({ default: val.ChatBotApi }))
)
const TestAPI = lazy(() => import('../components/Project/UI/TestAPI').then((val) => ({ default: val.TestAPI })))
const localStorageClearSkip = {
  hasRequestedNotification: true,
}

const CondCacheProcessor = () => {
  const dispatch = useDispatch()
  const __cond__cache__ = useSelector((state) => state.getIn(['__cond__cache__'], Map()), isSame)

  useEffect(() => {
    if (__cond__cache__.get('processed', Map()).size) {
      dispatch(processConditionQueCache())
    }
  }, [__cond__cache__])

  return null
}

const flipToast = (pos, isRTL) => {
  if (isRTL) {
    switch (pos) {
      case 'top-left':
        return 'top-right'
      case 'top-right':
        return 'top-left'
      case 'bottom-left':
        return 'bottom-right'
      case 'bottom-right':
        return 'bottom-left'
      default:
        return pos
    }
  } else {
    return pos
  }
}

const getPrimaryFontColor = (type) => {
  switch (type) {
    case 'web':
    case 'project':
      return '#fff'
    case CHAT_BOT_TYPE:
      return '#000'
    case WEB_FORM_TYPE:
      return '#000'
    case SLACK_BOT_TYPE:
      return '#fff'
    case 'survey':
    case 'surveyEmail':
      return '#000'
    default:
      return '#fff'
  }
}

const getPrimaryVariantColor = (type) => {
  switch (type) {
    case 'web':
    case 'project':
      return '#f03cc2'
    case WEB_FORM_TYPE:
      return '#10DDE5'
    case CHAT_BOT_TYPE:
      return '#0cb1ff'
    case SLACK_BOT_TYPE:
      return '#ff2626'
    case 'survey':
    case 'surveyEmail':
      return '#ceab01'
    case 'sfForm':
      return '#1E48D8'
    default:
      return '#f03cc2'
  }
}

const getPrimaryColor = (type) => {
  switch (type) {
    case 'web':
    case 'project':
      return '#E011AC'
    case WEB_FORM_TYPE:
      return '#10DDE5'
    case CHAT_BOT_TYPE:
      return '#0cb1ff'
    case SLACK_BOT_TYPE:
      return '#ff2626'
    case 'survey':
    case 'surveyEmail':
      return '#EDD826'
    case 'sfForm':
      return '#1E48D8'
    default:
      return '#E011AC'
  }
}

const DEF_LOAD_IMG =
  'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTMiIGhlaWdodD0iNDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgICA8ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxwYXRoIGQ9Im01MC4yMTUgMTYgLjM1Mi40OTdhMTMuMTY3IDEzLjE2NyAwIDAgMSAwIDE1LjI1MkwzOS4yNyA0Ny42OTFhLjczNS43MzUgMCAwIDEtLjYuMzA5aC04LjkzOWEuNzMzLjczMyAwIDAgMS0uNjUtLjM5LjcxNi43MTYgMCAwIDEgLjA1LS43NDlMNDguOTYxIDE4Ljg4QTcuNjU3IDcuNjU3IDAgMCAwIDUwLjIxNSAxNnoiIGZpbGw9IiNFMDExQUMiLz4KICAgICAgICA8cGF0aCBkPSJNMzguNzA1IDBjLjI0IDAgLjQ2NS4xMTYuNjAyLjMxMWw3LjczIDEwLjk2MmE1LjMyIDUuMzIgMCAwIDEtLjAxMiA2LjEyMkw0My43NzggMjJjLS4xLS4xOTktLjIxNi0uMzktLjM0NS0uNTcybC0xNC4zLTIwLjI4YS43MjUuNzI1IDAgMCAxLS4wNS0uNzU1LjczNi43MzYgMCAwIDEgLjY1Mi0uMzkzaDguOTd6IiBmaWxsPSIjM0ZENzM2Ii8+CiAgICAgICAgPHBhdGggZD0iTTkuMjIyIDI2Yy4xLjE5OS4yMTYuMzkuMzQ1LjU3MmwxNC4zIDIwLjI4YS43MjUuNzI1IDAgMCAxIC4wNS43NTUuNzM2LjczNiAwIDAgMS0uNjUyLjM5M2gtOC45N2EuNzM2LjczNiAwIDAgMS0uNjAyLS4zMTFsLTcuNzMtMTAuOTYyYTUuMzIgNS4zMiAwIDAgMSAuMDExLTYuMTIyTDkuMjIyIDI2eiIgZmlsbD0iI0ZGNjU1MCIvPgogICAgICAgIDxwYXRoIGQ9Ik00LjA0IDI5LjEyMUE3LjY1OCA3LjY1OCAwIDAgMCAyLjc4NCAzMmwtLjM1Mi0uNDk3YTEzLjE2OCAxMy4xNjggMCAwIDEgMC0xNS4yNTJMMTMuNzMuMzA5YS43MzUuNzM1IDAgMCAxIC42LS4zMDloOC45MzljLjI3MyAwIC41MjMuMTUuNjUuMzlhLjcxNi43MTYgMCAwIDEtLjA1Ljc0OUw0LjAzOSAyOS4xMnoiIGZpbGw9IiMxMERERTUiLz4KICAgIDwvZz4KPC9zdmc+Cg==")'

const getPreloaderStyle = (useCustom, image) => {
  let loaderImage = `background-image: var(--ft-loader-image)`

  return `
    :root{
        --ft-loader-image: ${useCustom && image && image != "'none'" ? `url(${image})` : DEF_LOAD_IMG};
    }
@keyframes flickerAnimation {
0%   { opacity:1; }
50%  { opacity:0.2; }
100% { opacity:1; }
}
@-o-keyframes flickerAnimation{
0%   { opacity:1; }
50%  { opacity:0.2; }
100% { opacity:1; }
}
@-moz-keyframes flickerAnimation{
0%   { opacity:1; }
50%  { opacity:0; }
100% { opacity:1; }
}
@-webkit-keyframes flickerAnimation{
0%   { opacity:1; }
50%  { opacity:0.2; }
100% { opacity:1; }
}
body.loading{
position: relative;
}
body.loading::before{
content: " ";
left: 0;
top: 0;
right: 0;
bottom: 0;
background-color: rgba(0,0,0,.1);
position: absolute;
z-index: 8888;
}
body.loading #__next::after{
content: " ";
${loaderImage};
top: 50%;
left: 50%;
transform: translate(-50%, -50%);
position: absolute;
-webkit-animation: flickerAnimation 1s infinite;
-moz-animation: flickerAnimation 1s infinite;
-o-animation: flickerAnimation 1s infinite;
animation: flickerAnimation 1s infinite;
z-index: 9999;
background-position: center;
background-repeat: no-repeat;
width: 68px;
height: 68px;
background-size: contain;
border-radius: 50%;
}`.replace(/\n/gim, '')
}

const STATIC_SCRIPT = `
try {
    function googleMapInited(name) {
       try{
            window.googleMapReady = true;
            window.geocoder = new google.maps.Geocoder();
       }catch(error){
       }
    }
    var windowWidth = window.innerWidth;
    var moveToDevice = 'lg';
    
    var parseURLParameters = function () {
        var result = {};
        try {
            var tmpURL = location.href;
            if (tmpURL.indexOf("?") > -1) {
                tmpURL = tmpURL.split("?")[1];
                if (tmpURL && typeof tmpURL == "string") {
                    tmpURL = tmpURL.replace("#/", "");
                };
                if (tmpURL && typeof tmpURL == "string" && tmpURL.indexOf("&") > -1) {
                    tmpURL = tmpURL.split("&");
                    for(var i=0; i< tmpURL.length;i++) {
                        var param = tmpURL[i];
                        if (typeof param == "string" && param.indexOf("=") > -1) {
                            param = param.split("=");
                            result[param[0]] = decodeURIComponent(param[1]);
                        } else {
                            result[param] = true;
                        }
                    };
                } else {
                    if (typeof tmpURL == "string" && tmpURL.indexOf("=") > -1) {
                        tmpURL = tmpURL.split("=");
                        result[tmpURL[0]] = decodeURIComponent(tmpURL[1]);
                    } else {
                        if (tmpURL) {
                            result[tmpURL] = true;
                        }
                    }
                }
            }
        } catch (err) {
            console.error(err);
        };
        return result;
    };
    var params = parseURLParameters();
    if(params.device) {
        moveToDevice = params.device;
    } else if(window.compiledDevicesStyle && window.compiledDevicesStyle.pageWidth && window.compiledDevicesStyle.pageWidth <= windowWidth) {
        moveToDevice = 'lg';
    } else if(windowWidth > ${DEVICE_DEFAULT_MAX_SIZE.lg}) {
        moveToDevice = 'lg';
    } else if(windowWidth > ${DEVICE_DEFAULT_MAX_SIZE.md}) {
        moveToDevice = 'md';
    } else if(windowWidth > ${DEVICE_DEFAULT_MAX_SIZE.sm}) {
        moveToDevice = 'sm';
    }else if(windowWidth > ${DEVICE_DEFAULT_MAX_SIZE.xs}) {
        moveToDevice = 'xs';
    } else {
        moveToDevice = 'ss';
    }
    if(moveToDevice != 'lg' && window.compiledDevicesStyle && window.compiledDevicesStyle[moveToDevice]) {
        for(key in window.compiledDevicesStyle[moveToDevice]) {
            var styleDOm = document.querySelector('style[data-uid="'+key+'"]');
            if(styleDOm && window.compiledDevicesStyle[moveToDevice][key]) {
                styleDOm.innerHTML = window.compiledDevicesStyle[moveToDevice][key]
            }
        }
    }
} catch(err) {
    console.error(err);
}
`.replace(/\r|\n|[\s]{2,}/g, '')

const CustomToast = ({ children, className, ...props }) => (
  <DefaultToast {...props} className={clsx(className, 'ft--theme--Toast--root')}>
    {children}
  </DefaultToast>
)

const HIDE_CAPTCHA_CSS = `.grecaptcha-badge { visibility: hidden; }`

const CDN_HOST_GALLERY = getCdnHost(CloudResourceType.GALLERY)
const CDN_HOST_HELP_DESK = getCdnHost(CloudResourceType.HELP_DESK)
const CDN_HOST_USER_IMAGES = getCdnHost(CloudResourceType.USER_IMAGES)
const CDN_HOST_FORM_PREVIEWS = getCdnHost(CloudResourceType.FORM_PREVIEWS)

const jss = create({ plugins: [...jssPreset().plugins, rtl()] })

export const AppCommon = ({ children }) => {
  const dispatch = useDispatch()
  const loc = useLocation()
  let page = '/project/preview'
  if (String(loc.pathname).startsWith('/project') || String(loc.pathname).startsWith('/fttitanfiles')) {
    page = '/project/builder'
  }
  const router = {
    pathname: loc.pathname,
    asPath: loc.pathname + loc.search,
  }
  const isSFFormProjectType = useSelector((state) => state.getIn(['ProjectType']) === 'sfForm')
  const projectType = useSelector((state) => state.getIn(['project', 'ProjectType'], 'web'))
  const lastUpdate = useSelector((state) => state.getIn(['project', 'lastUpdate'], null))
  const syncParams = useSelector((state) => state.getIn(['project', 'Settings', 'ui', 'syncParams'], false))
  const currentPage = useSelector((state) => state.getIn(['page'], ''))
  const projectID = useSelector((state) => state.getIn(['project', '_id']))
  const onloadProjectID = useRef(null)
  const projectURL = useSelector((state) => state.getIn(['project', 'URL']))
  const useCustomLoader = useSelector((state) => state.getIn(['project', 'Settings', 'ui', 'useCustomLoader'], false))
  const usePageLoader = useSelector((state) => state.getIn(['project', 'Settings', 'ui', 'usePageLoader'], true))
  const customLoaderImage = useSelector((state) =>
    state.getIn(
      ['project', 'Settings', 'theme', 'lg', 'sfPreloaderMsg', 'icon', 'backgroundImage'],
      DEFAULT_LOADERS[0].path
    )
  )
  const isAllowSurveySlides = useSelector(
    (state) =>
      Boolean(state.get('page')) &&
      state.hasIn(['pages', state.get('page'), 'Props']) &&
      Boolean(state.getIn(['pages', state.get('page'), 'Props', 'Grid', 0]))
  )
  const ESubmitAction = useSelector((state) =>
    state.getIn(['project', 'Settings', 'survey', 'canESubmit'])
      ? state.getIn(['project', 'Settings', 'survey', 'ESubmitAction'], List())
      : List()
  )
  const isPage404 = useSelector((state) => {
    let is404 = false
    state.getIn(['project', 'Pages'], List()).map((page) => {
      if (page && isImmutable(page) && page.get('_id') == currentPage && page.get('isPage404')) {
        is404 = true
      }
    })
    return is404
  })
  const onLogin = useSelector((state) => {
    if (state.getIn(['project', 'Settings', 'ui', 'events', 'isUseOnLoginEvent'], false)) {
      return state.getIn(['project', 'Settings', 'ui', 'events', 'onLogin'], List())
    } else {
      return List()
    }
  })
  const isRTL = useLangDirection()
  const toast = useSelector((state) => state.getIn(['project', 'Settings', 'ui', 'toast'], Map()))
  const theme = useMemo(() => {
    let theme = { ...mainTheme }
    const primaryColor = getPrimaryColor(isSFFormProjectType ? 'sfForm' : projectType)

    theme.palette.primary.main = primaryColor
    theme.palette.primary.light = primaryColor
    theme.palette.primary.dark = primaryColor
    theme.palette.secondary.main = primaryColor
    theme.palette.secondary.light = primaryColor
    theme.palette.secondary.dark = primaryColor
    return theme
  }, [projectType, isSFFormProjectType])

  const allowTestAPI = router.asPath.indexOf('ftcypresstestapi') !== -1
  const isModal = String(router.asPath).indexOf('_ftPageMode=modal') !== -1
  const isPreview = page !== '/project/builder'
  const isTitanFiles = getBuilderTypePage() === 'titanfiles'

  const style = useMemo(
    () => getPreloaderStyle(useCustomLoader, customLoaderImage),
    [useCustomLoader, customLoaderImage]
  )
  React.useEffect(() => {
    if (!projectID) {
      return undefined
    }
    if (page !== '/project/builder') {
      const processOnLoad = async () => {
        try {
          if (window.location && String(window.location.href).toLowerCase().includes('ftautopdf=')) {
            return false
          }
        } catch (err) {
          console.error(err)
        }
        if (!IS_SERVER) {
          dispatch(loadAppPrevStateIfExist())
          let localLastUpdate = ftGetLocalStorage(getProjectLocalKey(projectID))
          if (localLastUpdate != lastUpdate) {
            try {
              let keys = _.keys(localStorage)
              _.each(keys, (key) => {
                if (_.startsWith(key, projectID)) {
                  ftRemoveLocalStorage(key)
                }
              })
            } catch (error) {
              typeof window !== 'undefined' && console.error(COOKIE_ERROR)
            }
            ftSetLocalStorage(getProjectLocalKey(projectID), lastUpdate)
            ftRemoveLocalStorage(getSessionProjectLocaKey(projectURL))
          }
          if (syncParams) {
            let sessCookie = String(document.cookie)
              .split('; ')
              .find((row) => row.startsWith(SESS_COOKIE_KEY))
            if (sessCookie) {
              let data = ftGetLocalStorage(getSessionProjectLocaKey(projectURL))
              try {
                let params = JSON.parse(decompressFromUTF16(data))
                if (params) {
                  dispatch(ftProjectUpdateParams(params))
                }
              } catch {}
            } else {
              ftRemoveLocalStorage(getSessionProjectLocaKey(projectURL))
            }
          }

          window.__FT_STID__ = getQueryVariable('_ftPageID', uuid())
          const SYNC_MODE = window.location.href.indexOf('_ftPageMode=modal') !== -1 ? 'modal' : 'page'
          if (SYNC_MODE === 'page') {
            try {
              let keys = _.keys(window.localStorage)
              _.each(
                _.filter(
                  keys,
                  (key) =>
                    !_.startsWith(key, 'ftstd_') &&
                    !_.startsWith(key, 'e_cart_') &&
                    !_.startsWith(key, 'local_params_') &&
                    !_.startsWith(key, 'local_pagejobs_') &&
                    !_.startsWith(key, `offline_${projectID}`) &&
                    !_.startsWith(key, `${getSessionProjectLocaKey(projectURL)}`) &&
                    key !== getProjectLocalKey(projectID) &&
                    key != SESSION_TOKEN_NAME &&
                    key != SESSION_SALT_NAME &&
                    key !== OTP_SESSION_IS_ACTIVE
                ),
                (key) => {
                  if (!localStorageClearSkip[key]) {
                    ftRemoveLocalStorage(key)
                  }
                }
              )
            } catch (err) {
              console.error(COOKIE_ERROR)
            }
          }
          if (IS_INTERNAL_PREVIEW) {
            try {
              if (window.parent.__FT_PROJECT__API__) {
                let state = window.parent.__FT_PROJECT__API__.FTSyncState()
                if (state) {
                  dispatch(setProjectPreviewStore(state))
                  dispatch(projectPreviewSetOnLoadData(true))
                  dispatch(processRules(true))
                  dispatch(processPageOnLoad())
                }
              }
            } catch (err) {
              console.error(err)
            }
          } else {
            dispatch(projectPreviewSetOnLoadData(true))
            dispatch(projectPreviewDetectLocationAPI())
            if (!isPage404) {
              dispatch(initSFAutoTrigger())
              let lang = getQueryVariable('FTLang', '')
              dispatch(processWidgetSFMapping('onload', null, null, null, true, lang))
              dispatch(processRules(true))
              dispatch(processPageOnLoad())
              dispatch(processSFAutoTrigger(true))
              if (projectType === SURVEY_EMAIL_TYPE && isAllowSurveySlides) {
                dispatch(processButtonAction(ESubmitAction, 'ESubmitAction', false, null))
              }
              if (isFirstTimeLogin()) {
                try {
                  window.__FT__LOGIN_STATE__ = {}
                  const axios = getAxios()
                  const { status: refreshStatus, data: refreshData } = await axios.post(
                    `/webprojects/profile/refresh`,
                    {}
                  )
                  if (refreshData && refreshData.status) {
                    const dynamicData = document.getElementById('__FT__STATIC__')
                    if (dynamicData) {
                      let dd = JSON.parse(dynamicData.textContent)
                      dd.user = refreshData.username || 'Titan'
                      dd.messageValue = refreshData.username || 'Titan'
                      dd.svParams = refreshData.retParams || {}
                      dd.profileImage = refreshData.profileImage
                      dynamicData.textContent = JSON.stringify(dd)
                    }
                    dispatch(setClearSmartVParams())
                    dispatch(
                      setTargetParamsValue({
                        params: refreshData.retParams,
                        profileName: refreshData.username || 'Titan',
                      })
                    )
                    try {
                      __FT_PROJECT__API__.FTRefreshPWAPages()
                    } catch (error) {
                      console.error(error)
                    }
                  }
                } catch (error) {
                  console.log('cannot refresh smartv params', error)
                }
                dispatch(processButtonAction(onLogin, 'SmartVEvent', false, null))
              }
            }
          }
        }
      }
      if (onloadProjectID.current !== projectID) {
        onloadProjectID.current = projectID
        processOnLoad()
      }
    }
    if (document) {
      document.body.classList.remove('loading')
    }
    if (!IS_SERVER) {
      const cancelUpdatingSmartVSessionLeftTime = dispatch(processSetSessLeftTime(page !== '/project/builder'))
      const cancelUpdatingOtpSessionLeftTime = dispatch(processSetOtpSessionLeftTime(page !== '/project/builder'))

      return () => {
        if (typeof cancelUpdatingSmartVSessionLeftTime === 'function') {
          cancelUpdatingSmartVSessionLeftTime()
        }

        if (typeof cancelUpdatingOtpSessionLeftTime === 'function') {
          cancelUpdatingOtpSessionLeftTime()
        }
      }
    }
  }, [projectID])

  React.useEffect(() => {
    if (document) {
      document.body.classList.remove('loading')
      document.body.style['--ft--primary--color'] = getPrimaryColor(projectType)
      document.body.style['--ft--primary--font--color'] = getPrimaryFontColor(projectType)
      let el = document.getElementById('#__next')
      if (el) {
        el.style['--ft--primary--color'] = getPrimaryColor(projectType)
        el.style['--ft--primary--font--color'] = getPrimaryFontColor(projectType)
      }
    }
    if (!isPreview && ftGetLocalStorage('ft-view-tour-elem')) {
      document.body.classList.add('ft-view-tour-elem')
    }
  }, [projectType])

  useGlobalCssVariables()

  return (
    <>
      {isPreview && <JSScript type="text/javascript" html={STATIC_SCRIPT} />}
      <Helmet>
        <title>FormTitan</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
        <meta charSet="utf-8" />
        <meta name="referrer" content="always" />
        {isPreview ? (
          router.asPath.indexOf('ftnocache') === -1 ? null : (
            <script id="app-state" type="text/javascript" />
          )
        ) : null}
        <link rel="preconnect" href={`https://${CDN_HOST_GALLERY}`} />
        <link rel="preconnect" href={`https://${CDN_HOST_FORM_PREVIEWS}`} />
        <link rel="preconnect" href={`https://${CDN_HOST_USER_IMAGES}`} />
        <link rel="preconnect" href={`https://${CDN_HOST_HELP_DESK}`} />
        {page !== '/project/builder' && (
          <link href={`${process.env.REACT_APP_WEB_CDN}/wpstyles/web-project-preview.css`} rel="stylesheet" />
        )}
        {!isPreview && (
          <link
            href="https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap"
            rel="stylesheet"></link>
        )}
        <link
          href="https://fonts.googleapis.com/css?family=Alef|Assistant|David+Libre|Frank+Ruhl+Libre|Heebo|Miriam+Libre|Rubik|Secular+One|Suez+One|Varela+Round|Abril+Fatface|Arvo|Droid+Sans|Josefin+Slab|Old+Standard+TT|Open+Sans|PT+Sans|PT+Serif|Ubuntu|Vollkorn|Poppins|Roboto:400,300,700"
          rel="stylesheet"
          type="text/css"
        />
        {usePageLoader && <style type="text/css" dangerouslySetInnerHTML={{ __html: style }} />}
        {isModal && <style type="text/css" dangerouslySetInnerHTML={{ __html: HIDE_CAPTCHA_CSS }} />}
      </Helmet>
      {page === '/project/builder' && (
        <Helmet>
          <link
            key="shortcut-icon"
            rel="shortcut icon"
            href={`https://${CDN_HOST_HELP_DESK}/prod/favicon-transparent.ico`}
            type="image/x-icon"
          />
          <link
            rel="stylesheet"
            href="https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css"
            crossOrigin="anonymous"
          />
          <style key="styles_SalesforceSans" type="text/css" dangerouslySetInnerHTML={{ __html: STATIC_SF_FONT }} />
        </Helmet>
      )}
      <StylesProvider jss={jss}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <StyleElement
            uid="app-colors"
            style={`:root{--ft--primary--color: ${getPrimaryColor(projectType)};--ft--primary--color-variant: ${getPrimaryVariantColor(projectType)};--ft--primary--font--color: ${getPrimaryFontColor(projectType)};}`}
          />
          <FeatureStoreDataProvider>
            <ProfileProvider>
              <WebStatsigProvider>
                <ToastProvider
                  placement={flipToast(toast.get('placement', 'top-left'), isRTL)}
                  autoDismiss={toast.get('autoDismiss', true)}
                  autoDismissTimeout={~~toast.get('autoDismissTimeout', '5') * 1000}
                  components={{
                    Toast: CustomToast,
                  }}>
                  <LayoutProvider isPreview={isPreview}>
                    <ProjectStyles isPreview={isPreview} />
                    {children}
                  </LayoutProvider>
                </ToastProvider>
                {isPreview && !IS_SERVER && (
                  <>
                    <ProjectParameterSync />
                    <ProjectPersistSessionParameter />
                    <VideoEventsAPI />
                  </>
                )}
                <SuspenseLoader>
                  {!isPreview && !isTitanFiles && <BuilderAPI />}
                  {currentPage === 'chatBot' && <ChatBotApi />}
                  {allowTestAPI && <TestAPI />}
                </SuspenseLoader>
              </WebStatsigProvider>
            </ProfileProvider>
          </FeatureStoreDataProvider>
        </ThemeProvider>
      </StylesProvider>
      <SFActionProcessor />
      <CondCacheProcessor />
    </>
  )
}
